/**
 * SNS 共有用のボタングループ UI。
 */

import { isNativeApp } from '../platform/native';
import { track } from '../meta/telemetry';
import type { StageDefinition } from '../game/config/types';
import type { ShareVisual } from './ShareCard';
import {
  canShareImage,
  copyScoreText,
  downloadScoreCard,
  shareNative,
  shareScoreImage,
  shareTo,
  type ShareableResult,
} from './ShareSystem';

export class ShareButtons {
  private readonly container: HTMLElement;

  constructor(result: ShareableResult, stage: StageDefinition, visual?: ShareVisual) {
    this.container = document.createElement('div');
    this.container.className = 'share-buttons';
    this.container.setAttribute('role', 'group');
    this.container.setAttribute('aria-label', 'スコアを共有する');

    const nativeSupported = typeof navigator.share === 'function' || isNativeApp();
    // Instagram / TikTok には Web から投稿する URL が存在しないため、
    // 画像を OS の共有シートへ渡す経路がそれらへの唯一の導線になる。
    const imageShareSupported = canShareImage();

    const buttons: {
      label: string;
      icon: string;
      action: () => void;
      primary?: boolean;
      aria?: string;
    }[] = [
      ...(imageShareSupported
        ? [
            {
              label: '画像で共有',
              icon: '📸',
              action: () => void shareScoreImage(result, stage, visual),
              primary: true,
              aria: 'スコア画像を共有する（Instagram・TikTok・LINE など）',
            },
          ]
        : []),
      ...(nativeSupported
        ? [
            {
              label: '共有',
              icon: '⤴',
              action: () => void shareNative(result),
              primary: !imageShareSupported,
              aria: 'リンクを共有する',
            },
          ]
        : []),
      // 「𝕏 X」と二重に見えていたので、字は1つだけ
      { label: 'X', icon: '', action: () => shareTo('twitter', result), aria: 'Xでスコアを共有' },
      { label: 'LINE', icon: '💬', action: () => shareTo('line', result) },
      { label: 'コピー', icon: '📋', action: () => void copyScoreText(result) },
      {
        label: '画像保存',
        icon: '🖼',
        action: () => void downloadScoreCard(result, stage, visual),
        aria: 'スコア画像を保存する',
      },
    ];

    // ボタンが6つ並ぶと「もう一度あそぶ」が押し下げられるので、
    // いちばん使う1つだけを出し、残りは「ほかの共有」にたたむ
    const make = ({ label, icon, action, aria }: (typeof buttons)[number], primary: boolean) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = primary ? 'btn share-buttons__btn share-buttons__btn--main' : 'btn share-buttons__btn';
      btn.innerHTML = icon ? `${icon} ${escapeHtml(label)}` : escapeHtml(label);
      btn.setAttribute('aria-label', aria ?? `${label}でスコアを共有`);
      btn.addEventListener('click', () => {
        const method = SHARE_METHOD[label] ?? 'other';
        // 画像の共有は、どの名場面を載せたかも残す（例: image_share.level）
        const moment = visual?.moment?.kind;
        const withMoment = moment && (method === 'image_share' || method === 'image') ? `${method}.${moment}` : method;
        track('share', { detail: withMoment });
        void action();
      });
      return btn;
    };
    const mainIndex = Math.max(
      0,
      buttons.findIndex((b) => b.primary),
    );
    this.container.append(make(buttons[mainIndex], true));
    // LINE は日本でいちばん使われるので、たたまずに並べる
    const lineIndex = buttons.findIndex((b) => b.label === 'LINE');
    if (lineIndex >= 0 && lineIndex !== mainIndex) {
      const line = make(buttons[lineIndex], false);
      line.classList.add('share-buttons__btn--line');
      this.container.append(line);
    }
    const more = document.createElement('details');
    more.className = 'share-buttons__more';
    const summary = document.createElement('summary');
    summary.textContent = 'ほかの共有';
    const list = document.createElement('div');
    list.className = 'share-buttons__list';
    buttons.forEach((b, i) => {
      if (i !== mainIndex && i !== lineIndex) list.append(make(b, false));
    });
    more.append(summary, list);
    this.container.append(more);
  }

  mount(parent: HTMLElement): void {
    parent.append(this.container);
  }

  get element(): HTMLElement {
    return this.container;
  }
}

/** 遊び方の記録に残す共有の手段（ボタンの文字 → 記録の名前）。 */
const SHARE_METHOD: Record<string, string> = {
  画像で共有: 'image_share',
  共有: 'native',
  X: 'x',
  LINE: 'line',
  コピー: 'copy',
  画像保存: 'image',
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
